<script setup lang="ts">
import {ref, computed, onBeforeMount, onMounted, useTemplateRef, onUnmounted, nextTick} from 'vue';
import type {ComponentProps} from 'vue-component-type-helpers';

import {useAllSpiceAppStore} from '../../allspice_app/store.ts';

import {type ComponentLibraryFileType, type LibraryComponent} from './library_component.ts';
import EcadDocument from './EcadDocument.vue';
import ComponentLibraryDiffLine from './ComponentLibraryDiffLine.vue';
import {SvgIcon} from '../../svg.ts';
import PageLevelComments from '../../allspice_app/components/Previews/PageLevelComments.vue';
import type {FileDiffPreview} from '../../allspice_app/types/previews.ts';
import {hasCommentsForPage} from '../../allspice_app/components/Previews/page-comments.ts';
import type {Net} from './types.ts';

type AllspiceSnippetClickThruEvent = CustomEvent<{docId: string}>;
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- we need to extend an interface
  interface DocumentEventMap {
    'allspice-snippet-click-thru': AllspiceSnippetClickThruEvent,
  }
}

const observerSupported = window && 'IntersectionObserver' in window;

export type RenderParamsProp = Pick<ComponentProps<typeof EcadDocument>,
  | 'areDocumentsLoadedPromise' | 'isDiffLoadedPromise' |
  'isDiff' | 'diffType' |
  'baseRepoOwner' | 'baseRepoName' | 'baseCommit' | 'baseTreePath' |
  'headRepoOwner' | 'headRepoName' | 'headCommit' | 'headTreePath' |
  'alt' | 'svgClassNames' | 'colorProfile' | 'displayDefaultOnError' |
  'dataAsViewCoords' | 'dataAsAspectRatio'
>;

const props = withDefaults(defineProps<{
  component: LibraryComponent;
  nets: Record<string, Net>;
  headerClassNames: string;
  isDiff: boolean;
  isShown: boolean;
  fileType: ComponentLibraryFileType,
  renderParams?: RenderParamsProp,
  queryDocId?: string;
  initialExpand?: boolean;
  folded?: boolean;
}>(), {
  renderParams: null,
  queryDocId: '',
  initialExpand: false,
  folded: false,
});

const emit = defineEmits<{
  'collapse-component': [componentId: string];
}>();

const rowEl = useTemplateRef('row');
const accordionContentsEl = useTemplateRef('accordionContents');
const ecadDocumentEls = useTemplateRef('ecadDocument');

const accordionExpanded = ref<boolean>(props.initialExpand);
const thumbnailLoaded = ref<boolean>(false);
const ecadDocumentInitialized = ref<boolean>(false);
// TODO: rename this to something like `isExpandedByDefault`, or rename the prop and this to `expanded`
const initialExpandModel = ref<boolean>(props.initialExpand);
const inViewport = ref<boolean>(!observerSupported); // always true if IntersectionObserver isn't supported

const svgData = computed(() => {
  if (!props.component.outputSvg) {
    return '';
  }

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(props.component.outputSvg)}`;
});

const isSchematic = computed(() => {
  return props.fileType === 'schematic';
});

const hideAccordion = computed(() => {
  // if this is a schematic and initalExpand is true, that means that this is
  // the only page of the schematic, so we should hide the accordion.
  return isSchematic.value && props.initialExpand;
});

// Which properties appear in the summary (i.e., when the row is collapsed)
const summaryProperties = computed(() => {
  switch (props.fileType) {
    case 'schematic':
      return ['ui_group_name', 'name'];
    case 'library':
      return ['name', 'comment', 'description'];
    default:
      return [];
  }
});

const allspiceAppStore = useAllSpiceAppStore();
const preview = computed<FileDiffPreview | null>(() => {
  if (props.isDiff && allspiceAppStore?.activePreview.value.ty === 'file-diff') {
    return allspiceAppStore.activePreview.value;
  }
  return null;
});

const commentPath = computed(() => {
  return preview.value?.path;
});

const commentSubPath = computed(() => {
  return preview.value ? props.component.id : null;
});

const commentURL = computed(() => {
  return preview.value ? `${allspiceAppStore.repoLink}/pulls/${allspiceAppStore.prInfo.number}/files/reviews/new_comment` : null;
});

const codeConversationsContainer = computed(() => {
  return preview.value?.commentHtml;
});

const hasPageLevelComments = computed(() => {
  return hasCommentsForPage(codeConversationsContainer.value, props.component.id);
});

const isInDesignReview = computed(() => {
  return allspiceAppStore?.diffInfo?.issueLink !== null;
});

/**
 * Handle snippet click through events where the accordion is not expanded on page load
 */
function handleSnippetClickThrough(event: AllspiceSnippetClickThruEvent) {
  event.preventDefault();

  if (props.component.id === event.detail.docId && !accordionExpanded.value) {
    toggleAccordion(true);
  }
}

let collapseAnimationTimeoutId: ReturnType<typeof setTimeout> | null = null;
async function toggleAccordion(shouldExpand: boolean | null = null) {
  if (shouldExpand === null) {
    shouldExpand = !accordionExpanded.value;
  }

  if (shouldExpand) {
    if (collapseAnimationTimeoutId) {
      clearTimeout(collapseAnimationTimeoutId);
      collapseAnimationTimeoutId = null;
    }

    accordionExpanded.value = true;
    await nextTick(); // wait for the DOM changes before animating
    accordionContentsEl.value.style.display = 'flex';
    accordionContentsEl.value.style.maxHeight = `${accordionContentsEl.value.scrollHeight}px`;
    accordionContentsEl.value.addEventListener('transitionend', () => {
      accordionContentsEl.value.style.maxHeight = 'initial';
      accordionContentsEl.value.style.overflow = 'visible';
    }, {once: true});
    await initializeEcadDocument();
  } else {
    // TODO: what is this doing here?
    initialExpandModel.value = false;
    accordionContentsEl.value.style.maxHeight = '';
    accordionContentsEl.value.style.overflow = '';
    emit('collapse-component', props.component.id);

    // hide the SVG after the collapse animation is complete
    collapseAnimationTimeoutId = setTimeout(() => {
      accordionExpanded.value = false;
    }, 200);
  }
}

/**
 * Return the class of the diff icon to show (where applicable)
 */
function diffIconClass() {
  if (props.component.isAdded) {
    return 'octicon-diff-added';
  } else if (props.component.isRemoved) {
    return 'octicon-diff-removed';
  } else if (props.component.isModified) {
    return 'octicon-diff-modified';
  }
  return '';
}

/**
 * Return the tooltip text for the diff icon
 */
function diffIconTitle() {
  if (props.component.isAdded) {
    return 'This component was added';
  } else if (props.component.isRemoved) {
    return 'This component was removed';
  } else if (props.component.isModified) {
    return 'This component was modified';
  }
  return '';
}

/**
 * Bubble up an event to the component library to relocate and rerender the inspector
 */
function showLibraryInspector(event: MouseEvent) {
  event.preventDefault();

  const libraryInspectorClickEvent = new CustomEvent('library-inspector-click', {
    bubbles: true,
    detail: {
      clientX: event.clientX,
      clientY: event.clientY,
      componentDataHead: props.component.head,
      componentDataBase: props.component.base,
      componentId: props.component.id,
    },
  });
  (event.target as Element).closest('.component-library').dispatchEvent(libraryInspectorClickEvent);
}

async function initializeEcadDocument() {
  // TODO: does this ever get called when ecadDocumentInitialized is already true?
  if (ecadDocumentInitialized.value) {
    console.debug('handleEcadDocumentAllLoad called when ecadDocumentInitialized was true');
  }

  if (isSchematic.value && !ecadDocumentInitialized.value) {
    requestAnimationFrame(() => {
      // stop waiting for the EcadDocument components if the accordion is closed
      if (!accordionExpanded.value) {
        return;
      }

      // if the EcadDocument components aren't mounted yet (how often does this happen?), schedule another run
      if (!ecadDocumentEls.value) {
        initializeEcadDocument();
        return;
      }

      // Now that it's mounted, do what we need to
      ecadDocumentInitialized.value = true;
      for (const ecadDocComponent of ecadDocumentEls.value) {
        ecadDocComponent.onHeadSvgLoad();
      }

      accordionContentsEl.value.style.maxHeight = `${accordionContentsEl.value.scrollHeight}px`;
    });
  }
}

let observer: IntersectionObserver | null = null;
function attachIntersectionObserver() {
  if (!observerSupported) {
    return;
  }

  if (observer !== null) {
    detachIntersectionObserver();
  }

  observer = new IntersectionObserver((entries) => {
    const isIntersecting = entries.some((entry) => entry.isIntersecting);
    inViewport.value = isIntersecting;
  });

  observer.observe(rowEl.value);
  // Fix for Firefox, which only runs IntersectionObserver when a RAF happens.
  requestAnimationFrame(() => {});
}
function detachIntersectionObserver() {
  if (observer === null) {
    return;
  }

  observer.disconnect();
  observer = null;
}

// TODO: I think this could probably be done in a different way using watches and props
onBeforeMount(() => {
  const hashSubpath = allspiceAppStore?.activeHash.value.split('_')[1] ?? null;

  // expand this row if it matches the docId in the URL query
  initialExpandModel.value = initialExpandModel.value || props.queryDocId === props.component.id || hashSubpath === props.component.id;
  accordionExpanded.value = initialExpandModel.value;
});

onMounted(() => {
  document.addEventListener('allspice-snippet-click-thru', handleSnippetClickThrough);

  attachIntersectionObserver();

  // if we start with this row expanded, scroll it into view so that the user sees it immediately
  if (initialExpandModel.value) {
    rowEl.value.scrollIntoView();
  }
});

onUnmounted(() => {
  document.removeEventListener('allspice-snippet-click-thru', handleSnippetClickThrough);

  detachIntersectionObserver();
});

</script>

<template>
  <div ref="row">
    <div
      v-if="!hideAccordion"
      class="component-row"
      :aria-expanded="accordionExpanded"
      :class="component.classNames + (isShown ? '' : ' hidden-component')"
      :data-accordion-head="props.component.id"
      @click.prevent="() => toggleAccordion()"
    >
      <div class="accordion-toggle-button">
        <a
          href="#"
          @keypress.enter.prevent="() => toggleAccordion()"
        >
          <svg
            v-if="accordionExpanded"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            width="16"
            height="16"
          >
            <path d="M12.78 5.22a.749.749 0 0 1 0 1.06l-4.25 4.25a.749.749 0 0 1-1.06 0L3.22 6.28a.749.749 0 1 1 1.06-1.06L8 8.939l3.72-3.719a.749.749 0 0 1 1.06 0Z" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            width="16"
            height="16"
          >
            <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z" />
          </svg>
        </a>
      </div>
      <img
        v-show="thumbnailLoaded"
        v-if="inViewport"
        class="component-thumbnail"
        :src="svgData"
        alt="Thumbnail"
        height="90"
        @load="thumbnailLoaded=true"
      >
      <div
        v-show="!thumbnailLoaded || !inViewport"
        class="thumbnail-loading"
      >
        <div class="is-loading tw:size-10" />
      </div>
      <template
        v-for="key in summaryProperties"
        :key="key"
      >
        <div class="component-row-cell">
          <ComponentLibraryDiffLine
            :component="component"
            :path="[key]"
            :cell-class-names="key"
          />
        </div>
      </template>
      <div class="component-row-has-comments-icon">
        <SvgIcon
          v-if="hasPageLevelComments"
          name="octicon-comment-discussion"
          class="component-row-icon"
          title="This component has component-specific comments. Expand the row to see them."
        />
      </div>
      <div
        v-if="isDiff"
        class="component-row-diff-icon"
      >
        <SvgIcon
          v-if="component.hasChanged()"
          :name="diffIconClass()"
          :class="['svg-icon', diffIconClass(), 'component-row-icon']"
          :title="diffIconTitle()"
        />
      </div>
    </div>
    <div
      v-show="accordionExpanded"
      ref="accordionContents"
      class="accordion-contents"
      :class="headerClassNames + (isShown ? '' : ' hidden-component') + (initialExpandModel ? ' default-expanded' : '')"
      :data-accordion-content="props.component.id"
      :data-path="commentPath"
      :data-sub-path="commentSubPath"
      :data-new-comment-url="commentURL"
    >
      <div
        class="component-data-row"
        :class="{ 'tw:p-0': hideAccordion }"
      >
        <template v-if="isSchematic">
          <div class="component-data">
            <template v-if="!hideAccordion">
              <div class="component-data-header">
                <ComponentLibraryDiffLine
                  :component="component"
                  :path="['ui_group_name']"
                  cell-class-names="title"
                />
                <div
                  v-if="props.isDiff && isInDesignReview"
                  class="component-data-header-actions"
                >
                  <a
                    class="review-page-comment"
                    style="text-decoration: none;"
                  >
                    <span
                      class="btn-octicon tooltip"
                      data-tooltip-content="Comment on page"
                      data-position="bottom-center"
                      data-variation="inverted tiny"
                    >
                      <SvgIcon
                        name="octicon-comment"
                        class="tw:flex tw:content-center tw:justify-center"
                      />
                    </span>
                  </a>
                </div>
              </div>
              <div class="component-data-header">
                <ComponentLibraryDiffLine
                  :component="component"
                  :path="['name']"
                  cell-class-names="title"
                />
              </div>
            </template>
            <div
              v-if="accordionExpanded"
              class="component-data-content"
            >
              <EcadDocument
                v-for="symbol in component.outputSymbols"
                :key="symbol.id.toString()"
                ref="ecadDocument"
                :base-document="component.base"
                :head-document="component.head"
                :nets="nets"
                :are-documents-loaded-promise="renderParams.areDocumentsLoadedPromise"
                :head-svg="symbol.svgText"
                :is-diff-loaded-promise="renderParams.isDiffLoadedPromise"
                :is-diff="renderParams.isDiff"
                :base-repo-owner="renderParams.baseRepoOwner"
                :base-repo-name="renderParams.baseRepoName"
                :base-commit="renderParams.baseCommit"
                :base-tree-path="renderParams.baseTreePath"
                :head-repo-owner="renderParams.headRepoOwner"
                :head-repo-name="renderParams.headRepoName"
                :head-commit="renderParams.headCommit"
                :head-tree-path="renderParams.headTreePath"
                :diff-type="renderParams.diffType"
                :alt="renderParams.alt"
                :svg-class-names="renderParams.svgClassNames"
                :data-as-view-coords="renderParams.dataAsViewCoords"
                :data-as-aspect-ratio="renderParams.dataAsAspectRatio"
                :display-default-on-error="renderParams.displayDefaultOnError"
                :color-profile="renderParams.colorProfile"
                :doc-id="symbol.docId"
                :deferred-render="!initialExpandModel"
                :initial-snippet-clickthrough="initialExpandModel"
                :folded="folded"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="component-display-col">
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-for="symbol in component.outputSymbols"
              :key="symbol.id.toString()"
              class="component-display"
              @click="showLibraryInspector"
              v-html="symbol.svgText"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
          <div class="component-data">
            <div class="component-data-header">
              <ComponentLibraryDiffLine
                :component="component"
                :path="['name']"
                cell-class-names="title"
              />
              <div
                v-if="props.isDiff && isInDesignReview"
                class="component-data-header-actions"
              >
                <a
                  class="review-page-comment"
                  style="text-decoration: none;"
                >
                  <span
                    class="btn-octicon tooltip"
                    data-tooltip-content="Comment on page"
                    data-position="bottom-center"
                    data-variation="inverted tiny"
                  >
                    <SvgIcon
                      name="octicon-comment"
                      class="tw:flex tw:content-center tw:justify-center"
                    />
                  </span>
                </a>
              </div>
            </div>
            <div class="component-data-subheader">
              <ComponentLibraryDiffLine
                :component="component"
                :path="['description']"
              />
            </div>
            <div class="component-data-table">
              <table>
                <tbody>
                  <tr>
                    <td>Part ID</td>
                    <td>
                      <ComponentLibraryDiffLine
                        :component="component"
                        :path="['part_id']"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Comment</td>
                    <td>
                      <ComponentLibraryDiffLine
                        :component="component"
                        :path="['comment']"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="component.hasProperty(['attributes'])"
              class="component-data-table"
            >
              <div class="ui divider" />
              <div class="component-data-subheader">
                Attributes
              </div>
              <table
                class="component-attributes"
                :class="!component.isDiff ? 'alternating' : ''"
              >
                <tbody>
                  <tr
                    v-for="attributeKey in component.allChildKeys(['attributes'])"
                    :key="attributeKey"
                  >
                    <td>
                      <ComponentLibraryDiffLine
                        :component="component"
                        :path="['attributes', attributeKey, 'name']"
                      />
                    </td>
                    <td>
                      <ComponentLibraryDiffLine
                        :component="component"
                        :path="['attributes', attributeKey, 'value']"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
      <div
        class="component-comments"
      >
        <PageLevelComments
          :code-conversations-container="codeConversationsContainer"
          :page-id="props.component.id"
          :warn-for-other-pages="false"
        />
      </div>
    </div>
    <div
      class="ui divider component"
      style="padding: 0 !important"
    />
  </div>
</template>

<!-- eslint-disable vue-scoped-css/no-unused-selector -->
<style scoped>

.component-thumbnail {
  /* This should match the header row. */
  flex: 0 0 125px;
  min-width: 125px;
  width: 125px;
  /* SVGs already have a built-in margin. */
  padding: 0 !important;
}

.component-display {
  width: 300px;
}

.component-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
}

.component-row-cell {
  padding: 10px 15px 10px 25px;
  text-align: left;
  word-break: break-all;
  flex-basis: 150px;
  flex-grow: 1;
}

.component-row-has-comments-icon,
.component-row-diff-icon {
  width: 30px;
  flex:none;
}

.hidden-component {
  background: var(--color-markup-table-row);
}

.default-expanded {
  max-height: initial !important;
}

.accordion-toggle-button {
  /* This should match the header row. */
  flex: 0 0 30px;
  padding-left: 10px;
  padding-right: 10px;
  align-self: center;
}

.accordion-contents {
  text-align: left;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease-out;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.accordion-contents th, td {
  padding: 6px 13px;
  text-align: left;
}

.accordion-contents td {
  max-width: 400px;
  word-wrap: break-word;
  border-left: none !important;
}

.accordion-contents tr.added > th,
.accordion-contents tr.added > td {
  background: var(--color-diff-added-row-bg);
}

.accordion-contents tr.removed > th,
.accordion-contents tr.removed > td {
  background: var(--color-diff-removed-row-bg);
}

.component-row.added,
.accordion-contents.added {
  background: var(--color-diff-added-row-bg);
}

.component-row.removed,
.accordion-contents.removed {
  background: var(--color-diff-removed-row-bg);
}

.component-data {
  padding-left: 10px;
  width: 100%;
}

.component-data-table {
  width: 100%;
}

.component-data-header {
  display: flex;
  align-items: center;

  font-size: 30px;
  padding-left: 13px;
  padding-bottom: 30px;
}

.component-data-header > div {
  flex: auto;
}

.component-data-header > div.component-data-header-actions {
  flex: none;
}

.component-data-subheader {
  font-size: 18px;
  padding-left: 13px;
  padding-bottom: 15px;
}

.component-data-row {
  display: flex;
  flex-direction: row;
  padding: 25px;
}

.component-attributes.alternating tr:nth-child(2n) {
  background: var(--color-markup-table-row);
}

.ui.divider.component {
  margin: 0;
}

.svg-icon.octicon-diff-modified {
  color: var(--color-yellow);
}

.svg-icon.octicon-diff-added {
  color: var(--color-green);
}

.svg-icon.octicon-diff-removed {
  color: var(--color-red);
}

.component-row-icon {
  padding-right: 4px;
}

.thumbnail-loading {
  min-height: 90px;
  min-width: 125px;
  padding-top: 30px;
  padding-left: 45px;
}

@media only screen and (max-width: 900px) {
  .component-display {
    width: 150px;
  }

  .component-data-row {
    padding: 5px;
  }

  .component-data {
    padding-left: 5px;
  }

  .accordion-contents td {
    max-width: 200px;
  }
}

</style>
